<template>
  <div class="c_atom_accordion__item" :data-theme="theme">
    <button
      class="c_atom_accordion__header"
      @click="toggleAccordion"
      :id="`c_atom_accordion__header--${itemIndex}`"
      :aria-expanded="isAccordionOpen ? true : false"
      :aria-controls="`c_atom_accordion__content--${itemIndex}`"
      :aria-label="`${$t('Open this panel')} - ${title}`"
    >
      <slot name="title">{{ title }}</slot>
      <IconSVG :handle="isAccordionOpen ? 'arrow_up' : 'arrow_down'" width="15" height="20" />
    </button>
    <transition name="c_atom_accordion__toggle_animation-">
      <div
        class="c_atom_accordion__content"
        v-show="isAccordionOpen"
        :aria-labelledby="`c_atom_accordion__header--${itemIndex}`"
        role="region"
        :id="`c_atom_accordion__content--${itemIndex}`"
      >
        <slot name="content">
          <div v-html="content"></div>
        </slot>
      </div>
    </transition>
  </div>
</template>

<script>
import IconSVG from 'Components/icon_svg/IconSVG.vue';
import { defineComponent, reactive, toRefs, computed, onMounted } from '@nuxtjs/composition-api';

export default defineComponent({
  name: 'AccordionItem',
  components: {
    IconSVG,
  },
  props: {
    defaultOpen: { type: Boolean, default: false },
    allowMultiple: { type: Boolean, default: true },
    itemIndex: { type: Number },
    open: { type: Boolean, default: null },
    title: { type: String, default: '' },
    content: { type: String, default: '' },
    openBehavior: { type: String, default: 'first' },
    theme: { type: String, default: 'dark' }, // 'dark', 'light'
  },
  setup(props, { emit }) {
    const state = reactive({
      openBehavior: props?.openBehavior ?? 'first', // 'all', 'first', 'none'
      openState: props?.defaultOpen ?? false,
    });

    const isAccordionOpen = computed(() => (props.open == null ? state.openState : props.open));

    const toggleAccordion = () => {
      if (props?.open === null) {
        if (props.allowMultiple) {
          state.openState = !state.openState;
        } else {
          emit('requestOpen', props.itemIndex);
        }
      }
    };
    
    const closeAll = () => {
      state.openState = false;
    };

    const openSpecific = (i) => {
      state.openState = i === props.itemIndex;
    };

    onMounted(() => {
      const behaviors = {
        first: props.itemIndex === 0 ? true : false,
        all: true,
        none: false,
      };
      state.openState = behaviors[state.openBehavior];
    });

    return { ...toRefs(state), isAccordionOpen, toggleAccordion, closeAll, openSpecific };
  },
});
</script>
<style lang="scss">
.c_atom_accordion__item {
  &[data-theme='dark'] {
    --accordion-bg: var(--color-black);
    --accordion-divider: var(--color-yellow);
    --accordion-btn-hover: rgba(255, 255, 255, 0.08);
    --accordion-btn-color: var(--color-white);
  }
  &[data-theme='light'] {
    --accordion-bg: var(--color-white);
    --accordion-divider: var(--color-gray_230);
    --accordion-btn-hover: rgba(0, 0, 0, 0.03);
    --accordion-btn-color: var(--color-black);
  }
  display: flex;
  flex-direction: column;
  gap: px_to_rem(10);
  border-bottom: 1px solid var(--accordion-divider);
  .c_atom_accordion__header {
    @include font('graphik_semibold');
    font-size: px_to_rem(16);
    line-height: px_to_rem(24);
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    background-color: var(--accordion-bg);
    text-align: left;
    padding: px_to_rem(16) px_to_rem(0);
    border: none;
    transition: background-color cubic-bezier(0.4, 0, 0.6, 1) 0.15s;
    color: var(--accordion-btn-color);
    &:hover {
      background-color: var(--accordion-btn-hover);
    }
    .c_icon_svg {
      margin-right: px_to_rem(12);
    }
  }
  .c_atom_accordion__toggle_animation--enter-active,
  .c_atom_accordion__toggle_animation--leave-active {
    transition:
      opacity cubic-bezier(0.4, 0, 0.6, 1) 0.15s,
      transform cubic-bezier(0.4, 0, 0.6, 1) 0.15s;
  }
  .c_atom_accordion__toggle_animation--enter,
  .c_atom_accordion__toggle_animation--leave-to {
    opacity: 0;
    transform: translateY(-10px);
  }
}
</style>
